import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { skipWhile, take, takeUntil } from 'rxjs/operators';

// Store
import { EsignatureActions, EsignatureSelectors } from '@amfam/policy/e-signature/data-access';
import { RiskCompare, RiskModel } from '@amfam/policy/models';
import { PartyService } from '@amfam/profile/data-access';
import { AnalyticsFacade, PageAnalyticParams } from '@amfam/shared/analytics';
import { userQuery } from '@amfam/shared/user';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { ImpersonateRolesService, ImpersonationRoles } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { UtilService } from '@amfam/shared/utility/shared-services';
import { LoadingSpinnerService, PageTitleService, ValidationService } from '@amfam/ui-kit';

import * as fromRoot from '../../core/store';
import { KydSignUp } from '../../core/store/programs/kyd';
import * as kydEnrollActions from '../../core/store/programs/kyd/kyd-enroll.actions';
import { KydService } from './shared';

@Component({
  selector: 'app-kyd',
  templateUrl: './kyd.component.html',
  styleUrls: ['./kyd.component.scss'],
  standalone: false
})
export class KnowYourDriveComponent implements OnInit, OnDestroy {
  public customerId: string;
  public policyNumber: string;
  public submitted = false;
  public kydForm: UntypedFormGroup;
  public risks = new Array<RiskModel>();
  public selectedRisks = new Array<RiskModel>();
  private signUps = new Array<KydSignUp>();
  private emailAddress: string;
  private phoneNumber: string;

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  // **** Start of Analytics data for this component
  private pageAnalytic: PageAnalyticParams = {
    pageName: 'MyAccount:KnowYourDrive:SignUp',
    experience: '',
    primaryCategory: 'My Account',
    subCategory1: 'KnowYourDrive',
    subCategory2: 'Sign Up',
    subCategory3: ''
  };
  private sendAnalytic: PageAnalyticParams = {
    pageName: 'MyAccount:KnowYourDrive:SignUpComplete',
    experience: '',
    primaryCategory: 'My Account',
    subCategory1: 'KnowYourDrive',
    subCategory2: 'Sign Up',
    subCategory3: ''
  };

  public NEW_ADDRESS_TEXT = 'newShipTo';

  public formValid: boolean;

  // **** End of Analytics data for this component

  constructor(
    private route: ActivatedRoute,
    private spinner: LoadingSpinnerService,
    private pageTitle: PageTitleService,
    private formBuilder: UntypedFormBuilder,
    private store: Store<fromRoot.RootState>,
    private partyService: PartyService,
    private location: Location,
    private analyticsFacade: AnalyticsFacade,
    private utilService: UtilService,
    private kydService: KydService,
    public roleService: ImpersonateRolesService
  ) {
    // Subscribe to get the route params
    this.route.params.pipe(takeUntil(this.stop$)).subscribe(params => {
      const id = params['id'];
      this.policyNumber = id;
    });

    this.store
      .select(userQuery.selectCustomerId)
      .pipe(takeUntil(this.stop$))
      .subscribe(customerId => {
        this.customerId = customerId;
      });
  }

  ngOnInit() {
    this.pageTitle.set('KnowYourDrive');
    this.analyticsFacade.trackPage(this.pageAnalytic);

    this.initPartyDetails();

    this.buildForm();
  }

  private initPartyDetails() {
    this.store
      .select(userQuery.selectPartyDetails)
      .pipe(takeUntil(this.stop$))
      .subscribe(
        party => {
          if (party.primaryEmail.emailAddress) {
            this.emailAddress = party.primaryEmail.emailAddress;
          }
          if (party.primaryPhone.tenDigitPhoneNumber) {
            this.phoneNumber = party.primaryPhone.tenDigitPhoneNumber;
          }
        },
        error => {
          this.analyticsFacade.trackPageAndError(this.pageAnalytic, 'PartyLoadError');
          throw error;
        }
      );
  }

  private buildForm() {
    this.spinner.stop();

    this.kydForm = this.formBuilder.group({
      enroll: ['', Validators.required],
      terms: [false, Validators.compose([Validators.required, ValidationService.isChecked])],
      allRisks: [false]
    });

    this.store
      .select(fromRoot.getKydVehicleEnrollmentSummaries)
      .pipe(takeUntil(this.stop$))
      .subscribe(vehicleEnrollmentSummaries => {
        const eligibleRisksToEnroll = vehicleEnrollmentSummaries
          .filter(vehicleEnrollment => {
            const summary = vehicleEnrollment.enrollmentSummary;

            if (
              summary.enrollmentStatus === 'Opted-Out' ||
              summary.enrollmentStatus === 'Waiting Period'
            ) {
              let risk = vehicleEnrollment.risk;
              risk = Object.assign({}, risk, { previouslyEnrolled: true });
              vehicleEnrollment = Object.assign({}, vehicleEnrollment, { risk: risk });
            }

            return (
              summary.enrollmentStatus !== 'Enrolled' &&
              summary.enrollmentStatus !== 'Waiting Period'
            );
          })
          .map(vehicleEnrollment => vehicleEnrollment.risk);

        this.buildFormFields(eligibleRisksToEnroll);
        this.spinner.stop();
      }, this.errorHandler.bind(this));

    this.handleFormValueChange();
  }

  private handleFormValueChange() {
    this.kydForm.valueChanges.subscribe(() => {
      this.formValid = this.canSubmit();
    });
  }

  private buildFormFields(eligibleRisks: RiskModel[]) {
    for (const risk of eligibleRisks) {
      // Build kyd form components: add checkbox control(risk id) and the ship to form if the user adds an address
      this.kydForm.addControl(risk.id, new UntypedFormControl(''));
      // CustomAddressRequired validation is used for these fields. Basically when "newShipTo" is selected these fields are required.
      this.kydForm.addControl(
        'ship-' + risk.id,
        this.formBuilder.group({
          newDeviceInput: [
            false,
            ValidationService.requiredWhen(() => this.newDeviceInputRequired(risk))
          ],
          shipTo: ['', ValidationService.requiredWhen(() => this.shipToRequired(risk))],
          newAddress: this.formBuilder.group({
            addressLine1Input: [
              '',
              ValidationService.requiredWhen(() => this.customAddressRequired(risk))
            ],
            addressLine2Input: [''],
            cityInput: ['', ValidationService.requiredWhen(() => this.customAddressRequired(risk))],
            stateInput: [
              '',
              ValidationService.requiredWhen(() => this.customAddressRequired(risk))
            ],
            zipcodeInput: [
              '',
              ValidationService.validateWhen(
                () => this.customAddressRequired(risk),
                ValidationService.zipCodeValidator
              )
            ]
          })
        })
      );
    }

    this.risks = eligibleRisks
      .sort(RiskCompare.compare)
      .filter(risk => risk.vehicleTypeName === 'Car/Light Truck');

    if (eligibleRisks.length === 1) {
      this.selectRisk(this.risks[0], true);
    }
  }

  private errorHandler() {
    this.spinner.stop();
  }

  private newDeviceInputRequired(risk: RiskModel) {
    return (
      this.kydForm.controls[risk.id] &&
      this.kydForm.controls[risk.id].value &&
      risk.previouslyEnrolled
    );
  }

  private shipToRequired(risk: RiskModel) {
    if (!this.kydForm.controls[risk.id] || !this.kydForm.controls[risk.id].value) {
      return false;
    }

    if (risk.previouslyEnrolled) {
      return !risk.hasExistingDevice;
    }
    return true;
  }

  // This function is used by the inner new address fields. Basically when "newShipTo" is selected these fields are required.
  private customAddressRequired(risk: RiskModel) {
    if (!this.shipToRequired(risk)) {
      return false;
    }

    const formGroup = <UntypedFormGroup>this.kydForm.controls['ship-' + risk.id];
    if (formGroup) {
      return (
        formGroup.controls['shipTo'] && formGroup.controls['shipTo'].value === this.NEW_ADDRESS_TEXT
      );
    }
    return false;
  }

  public submitForm() {
    if (this.kydForm.valid) {
      this.submitted = true;
      this.spinner.start();
      this.store
        .select(EsignatureSelectors.selectESignatureRequired)
        .pipe(takeUntil(this.stop$))
        .subscribe(requiresSignature => {
          const signUps = this.selectedRisks
            .map(risk => this.signUps.find(signUp => signUp.riskModel.id === risk.id))
            .filter(signUp => !!signUp);
          if (requiresSignature) {
            this.spinner.start();
            this.kydService.storeSelectedRisks(signUps);
            this.store
              .pipe(select(BrandSelectors.selectExperienceId), take(1))
              .subscribe(experienceId => {
                const callbackUrl = this.utilService.getAbsoluteUrl(
                  'policies/knowyourdrive/signature',
                  experienceId
                );
                const signatureRequest = this.kydService.createSignatureRequest(callbackUrl);
                this.store.dispatch(EsignatureActions.SignESignature(signatureRequest));
              });
          } else {
            this.store.dispatch(new kydEnrollActions.EnrollAction(signUps));
            this.sendAnalytics();
          }
        });

      this.store
        .select(fromRoot.getKydEnrollmentConfirmationRefreshComplete)
        .pipe(
          takeUntil(this.stop$),
          skipWhile(refreshComplete => !refreshComplete)
        )
        .subscribe(refreshComplete => {
          this.spinner.stop();
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['/policies/knowyourdrive/confirmation']
            })
          );
        }, this.errorHandler.bind(this));
    }
  }

  public addressSelection(signUps: KydSignUp[]) {
    this.signUps = signUps;
  }

  private riskSelected(selectedRisk: RiskModel) {
    this.selectedRisks = this.risks
      .filter(risk => !!this.kydForm.controls[risk.id].value)
      .sort(RiskCompare.compare);

    const allRisksSelected = this.selectedRisks.length === this.risks.length;
    this.kydForm.controls['allRisks'].setValue(allRisksSelected);

    const formGroup = <UntypedFormGroup>this.kydForm.controls['ship-' + selectedRisk.id];
    for (const key in formGroup.controls) {
      if (formGroup.controls.hasOwnProperty(key)) {
        formGroup.controls[key].updateValueAndValidity();
      }
    }
  }

  private sendAnalytics() {
    this.analyticsFacade.trackPage(this.sendAnalytic);
  }

  public cancel() {
    this.location.back();
  }

  public selectAll(event) {
    const selected = event.target.checked;
    this.risks.forEach(risk => {
      this.selectRisk(risk, selected);
    });
  }

  private selectRisk(risk: RiskModel, selected: boolean) {
    const control = this.kydForm.controls[risk.id];
    if (control) {
      control.setValue(selected);
      this.riskSelected(risk);
    }
  }

  public canSubmit(): boolean {
    const hasNewAddrress = this.risks.some(risk => {
      const shipToGroup: UntypedFormGroup = <UntypedFormGroup>(
        this.kydForm.controls['ship-' + risk.id]
      );
      if (shipToGroup) {
        return shipToGroup.controls['shipTo'].value === this.NEW_ADDRESS_TEXT;
      }
      return false;
    });

    return (
      !hasNewAddrress && !this.submitted && this.kydForm.valid && this.selectedRisks.length > 0
    );
  }

  get roleName(): string {
    return ImpersonationRoles.SIGNUP_KYD;
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
    this.spinner.stop();
  }
}
